import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SimulationRunner from './SimulationRunner';
import ClientPersonaEditor from './ClientPersonaEditor';
import ClientPersonaLibrary from './personaLibrary/ClientPersonaLibrary'; // Phase 2: faceted persona library browser
import LibraryHealthDashboard from './personaLibrary/LibraryHealthDashboard'; // Phase 4: curation snapshot
import TherapistPromptEditor from './TherapistPromptEditor';
import SimulationHistoryViewer from './SimulationHistoryViewer';
import ReviewPromptEditor from './ReviewPromptEditor';
import UserManagement from './UserManagement';
import PlatformSettingsManager from './PlatformSettingsManager';
import ActivityLogViewer from './ActivityLogViewer'; // Import ActivityLogViewer
import TherapistAssignmentSettings from './TherapistAssignmentSettings'; // Import TherapistAssignmentSettings
import AiModeChangeSettings from './AiModeChangeSettings'; // Import AI Mode Change Settings
import ContentSafetySettings from './ContentSafetySettings'; // Import Content Safety Settings
import TherapistToolRegistry from './TherapistToolRegistry'; // Import Therapist Tool Registry
import PersonaToolAssignments from './PersonaToolAssignments'; // Import Persona Tool Assignments
import AssessmentDefinitionManager from './AssessmentDefinitionManager'; // Import Assessment Definition Manager
import MessageArchivalSettings from './MessageArchivalSettings'; // Import Message Archival Settings
import ContextMemorySettings from './ContextMemorySettings'; // Context & memory architecture settings
import SafetyMonitoring from './SafetyMonitoring'; // Alert notification mode + daily Gen 2 safety summary
import CallAudioRetention from './CallAudioRetention'; // Keep live-call audio for testing, and for how long
import SessionSecuritySettings from './SessionSecuritySettings'; // Idle timeout and sign-in length (security batch 2)
import NotificationConfigManager from './NotificationConfigManager'; // Import Notification Config Manager
import WebsiteEnquiryManager from './WebsiteEnquiryManager'; // Import Website Enquiry Manager
import TTSConfigurationManager from './tts/TTSConfigurationManager'; // Import TTS Configuration Manager
import ProviderPermissionsManager from './ProviderPermissionsManager'; // Import Provider Permissions Manager
import ManageStaff from './staff/ManageStaff'; // Import Manage Staff
import ServiceGroupsManagement from './ServiceGroupsManagement'; // Import Service Groups Management
import ScheduleOversight from '../shared/ScheduleOversight';
import ConsentDocumentManager from './ConsentDocumentManager';
import InvitationManagement from './InvitationManagement'; // Import Invitation Management
import OnboardingFlowsManager from './OnboardingFlowsManager'; // Import Onboarding Flows Manager
import AssignmentRulesManager from './AssignmentRulesManager'; // Import Assignment Rules Manager
import TriageReviewManager from './TriageReviewManager'; // Import Triage Review Manager
import TriageStrategyManager from './TriageStrategyManager'; // Import Triage Strategy Manager
import AlertsDashboard from './AlertsDashboard'; // Import Alerts Dashboard
import AdminReviewStats from './AdminReviewStats'; // Import Admin Review Stats
import ReviewTemplateManager from './ReviewTemplateManager'; // Import Review Template Manager
import ReviewTypeManager from './ReviewTypeManager'; // Import Review Type Manager
import AdvancedSimulationDashboard from './advancedSimulation/AdvancedSimulationDashboard'; // Import Advanced Simulation Dashboard
import { BatchProgressViewer } from './advancedSimulation'; // Re-exported from advancedSimulation/index.js
import LLMDiagnostics from './LLMDiagnostics'; // Import LLM Diagnostics Dashboard
import TimesOfServiceManager from './TimesOfServiceManager'; // Import Times of Service Manager
import ResearcherManagement from './ResearcherManagement'; // Import Researcher Management
import CBCSettingsManager from './CBCSettingsManager'; // Import CBC Settings Manager
import ParasocialConfigPanel from './ParasocialConfigPanel'; // Import Parasocial Detection Config Panel
import BoundaryMonitoringDashboard from './BoundaryMonitoring/BoundaryMonitoringDashboard'; // Import Boundary Monitoring Dashboard
import LLMConfigurationManager from './LLMConfigurationManager'; // Import LLM Configuration Manager
import LlmConfigManager from './LlmConfigManager'; // Unified per-component LLM config
import ScheduledJobsManager from './ScheduledJobsManager'; // Import Scheduled Jobs Manager
import SetupChecklist from './SetupChecklist'; // Import Setup Checklist
import SetupWizard from './SetupWizard'; // Import AI Setup Wizard
import SupportTicketsDashboard from './SupportTicketsDashboard'; // Import Support Tickets Dashboard
import SpecialistModuleManager from './SpecialistModuleManager'; // Import Gen 2 Specialist Module Manager
import TrainingScenarioAdmin from './TrainingScenarioAdmin'; // Import Training Scenario Admin
import VoiceAndAvatarManager from './VoiceAndAvatarManager'; // Voice catalog + avatar library

const AdminDashboard = ({ token, userRole }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('simulation'); // Default view
  const [currentSimId, setCurrentSimId] = useState(null);
  const [currentBatchId, setCurrentBatchId] = useState(null);

  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean); // e.g., ['admin', 'dashboard', 'advanced-simulation', 'run', '123']
    const view = pathParts[2]; // 'history', 'simulation', 'advanced-simulation', etc.
    const subView = pathParts[3]; // potential 'run' or simulationId
    const runId = pathParts[4]; // potential runId for advanced-simulation/run/:runId

    console.log('[AdminDashboard] Route parsed:', { pathParts, view, subView, runId, fullPath: location.pathname });
    console.log('[AdminDashboard] Checking condition: view === advanced-simulation?', view === 'advanced-simulation', 'subView === run?', subView === 'run', 'runId?', !!runId);

    if (view === 'history') {
      setActiveView('history');
      if (subView) {
        setCurrentSimId(subView);
        // SimulationHistoryViewer will use its own useParams to get this ID
      } else {
        setCurrentSimId(null);
      }
    } else if (view === 'advanced-simulation' && subView === 'run' && runId) {
      // Route: /admin/dashboard/advanced-simulation/run/:runId
      // Redirect to main advanced simulation dashboard (viewer handles details internally)
      console.log('[AdminDashboard] Redirecting old run detail route to advanced simulation dashboard');
      navigate('/admin/dashboard/advanced-simulation', { replace: true });
    } else if (view === 'advanced-simulation' && subView === 'batch' && runId) {
      // Route: /admin/dashboard/advanced-simulation/batch/:batchId
      // (`runId` here is the 4th path segment — happens to be the batchId in this route shape)
      setActiveView('advancedSimulationBatch');
      setCurrentBatchId(runId);
      setCurrentSimId(null);
    } else if (view === 'run-simulation') {
      setActiveView('simulation');
      setCurrentSimId(null);
    } else if (view === 'client-editor') {
      setActiveView('clientEditor');
      setCurrentSimId(null);
    } else if (view === 'persona-library') {
      setActiveView('personaLibrary');
      setCurrentSimId(null);
    } else if (view === 'persona-library-health') {
      setActiveView('personaLibraryHealth');
      setCurrentSimId(null);
    } else if (view === 'therapist-editor') {
      setActiveView('therapistEditor');
      setCurrentSimId(null);
    } else if (view === 'review-prompt-editor') {
      setActiveView('reviewPromptEditor');
      setCurrentSimId(null);
    } else if (view === 'user-management') {
      setActiveView('userManagement');
      setCurrentSimId(null);
    } else if (view === 'platform-settings') {
      setActiveView('platformSettings');
      setCurrentSimId(null);
    } else if (view === 'activity-log') { // Added for activity log
      setActiveView('activityLog');
      setCurrentSimId(null);
    } else if (view === 'therapist-assignment') { // Added for therapist assignment settings
      setActiveView('therapistAssignment');
      setCurrentSimId(null);
    } else if (view === 'ai-mode-change') { // Added for AI mode change settings
      setActiveView('aiModeChange');
      setCurrentSimId(null);
  } else if (view === 'content-safety') { // Added for content safety screening
    setActiveView('contentSafety');
    setCurrentSimId(null);
  } else if (view === 'tool-registry') { // Added for therapist tool registry
    setActiveView('toolRegistry');
    setCurrentSimId(null);
  } else if (view === 'persona-tool-assignments') { // Added for persona tool assignments
    setActiveView('personaToolAssignments');
    setCurrentSimId(null);
  } else if (view === 'assessment-definitions') { // Added for assessment definitions
    setActiveView('assessmentDefinitions');
    setCurrentSimId(null);
  } else if (view === 'message-archival') { // Added for message archival settings
    setActiveView('messageArchival');
    setCurrentSimId(null);
  } else if (view === 'context-memory') { // Context & memory architecture
    setActiveView('contextMemory');
    setCurrentSimId(null);
  } else if (view === 'safety-monitoring') { // Alert notification mode + daily safety summary
    setActiveView('safetyMonitoring');
    setCurrentSimId(null);
  } else if (view === 'call-audio-retention') { // Keep live-call audio for testing
    setActiveView('callAudioRetention');
    setCurrentSimId(null);
  } else if (view === 'sign-in-sessions') { // Idle timeout and sign-in length
    setActiveView('signInSessions');
    setCurrentSimId(null);
  } else if (view === 'notifications') { // Added for notification settings
    setActiveView('notifications');
    setCurrentSimId(null);
  } else if (view === 'website-enquiries') { // Added for website enquiries
    setActiveView('websiteEnquiries');
    setCurrentSimId(null);
  } else if (view === 'tts-configuration') { // Added for TTS configuration
    setActiveView('ttsConfiguration');
    setCurrentSimId(null);
  } else if (view === 'provider-permissions') { // Added for provider permissions
    setActiveView('providerPermissions');
    setCurrentSimId(null);
  } else if (view === 'manage-staff') { // Added for staff management
    setActiveView('manageStaff');
    setCurrentSimId(null);
  } else if (view === 'consent') { // Consent documents (AD-01)
    setActiveView('consent');
    setCurrentSimId(null);
  } else if (view === 'schedules') { // Therapist slots and bookings (AD-04)
    setActiveView('schedules');
    setCurrentSimId(null);
  } else if (view === 'service-groups') { // Added for service groups
    setActiveView('serviceGroups');
    setCurrentSimId(null);
  } else if (view === 'invitations') { // Added for client invitations
    setActiveView('invitations');
    setCurrentSimId(null);
  } else if (view === 'onboarding-flows') { // Added for onboarding flows
    setActiveView('onboardingFlows');
    setCurrentSimId(null);
  } else if (view === 'assignment-rules') { // Added for assignment rules
    setActiveView('assignmentRules');
    setCurrentSimId(null);
  } else if (view === 'triage-review') { // Added for triage review
    setActiveView('triageReview');
    setCurrentSimId(null);
  } else if (view === 'triage-strategies') { // Added for triage strategies
    setActiveView('triageStrategies');
    setCurrentSimId(null);
  } else if (view === 'alerts') { // Added for platform alert management
    setActiveView('alerts');
    setCurrentSimId(null);
  } else if (view === 'client-reviews') { // Added for client review statistics
    setActiveView('clientReviews');
    setCurrentSimId(null);
  } else if (view === 'review-templates') { // Added for review template management
    setActiveView('reviewTemplates');
    setCurrentSimId(null);
  } else if (view === 'review-types') { // Added for review type management
    setActiveView('reviewTypes');
    setCurrentSimId(null);
  } else if (view === 'advanced-simulation') { // Added for advanced simulation
    setActiveView('advancedSimulation');
    setCurrentSimId(null);
  } else if (view === 'llm-diagnostics') { // Added for LLM diagnostics
    setActiveView('llmDiagnostics');
    setCurrentSimId(null);
  } else if (view === 'times-of-service') { // Added for times of service
    setActiveView('timesOfService');
    setCurrentSimId(null);
  } else if (view === 'researcher-management') { // Added for researcher management
    setActiveView('researcherManagement');
    setCurrentSimId(null);
  } else if (view === 'cbc-settings') { // Added for CBC settings
    setActiveView('cbcSettings');
    setCurrentSimId(null);
  } else if (view === 'parasocial-detection') { // Added for parasocial detection settings
    setActiveView('parasocialDetection');
    setCurrentSimId(null);
  } else if (view === 'boundary-monitoring') { // Added for boundary monitoring dashboard
    setActiveView('boundaryMonitoring');
    setCurrentSimId(null);
  } else if (view === 'llm-configuration') { // Added for LLM configuration
    setActiveView('llmConfiguration');
    setCurrentSimId(null);
  } else if (view === 'llm-component-config') { // Unified per-component LLM config
    setActiveView('llmComponentConfig');
    setCurrentSimId(null);
  } else if (view === 'scheduled-jobs') { // Added for scheduled jobs management
    setActiveView('scheduledJobs');
    setCurrentSimId(null);
  } else if (view === 'setup-checklist') {
    setActiveView('setupChecklist');
    setCurrentSimId(null);
  } else if (view === 'setup-wizard') {
    setActiveView('setupWizard');
    setCurrentSimId(null);
  } else if (view === 'support-tickets') {
    setActiveView('supportTickets');
    setCurrentSimId(null);
  } else if (view === 'specialist-modules') {
    setActiveView('specialistModules');
    setCurrentSimId(null);
  } else if (view === 'training-scenarios') {
    setActiveView('trainingScenarios');
    setCurrentSimId(null);
  } else if (view === 'voice-and-avatar') {
    setActiveView('voiceAndAvatar');
    setCurrentSimId(null);
  } else if (pathParts.length === 2 && pathParts[0] === 'admin' && pathParts[1] === 'dashboard') {
      // Default to simulation view if only /admin/dashboard
      setActiveView('simulation');
      setCurrentSimId(null);
    }
    // Add other views as needed
  }, [location.pathname]);

  const handleNav = (viewPath) => {
    navigate(`/admin/dashboard/${viewPath}`);
  };

  const renderActiveView = () => {
    // SimulationHistoryViewer will now get simId from URL params via its own `useParams`
    // So we don't need to pass currentSimId as a prop directly to it here.
    // It will react to the URL change.
    switch (activeView) {
      case 'clientEditor':
        return <ClientPersonaEditor token={token} />;
      case 'personaLibrary':
        return <ClientPersonaLibrary token={token} userRole={userRole} />;
      case 'personaLibraryHealth':
        return <LibraryHealthDashboard token={token} />;
      case 'therapistEditor':
        return <TherapistPromptEditor token={token} />;
      case 'history':
        // Pass currentSimId (parsed from URL by AdminDashboard) to SimulationHistoryViewer
        return <SimulationHistoryViewer token={token} simulationIdFromUrl={currentSimId} />;
      case 'reviewPromptEditor':
        return <ReviewPromptEditor token={token} />;
      case 'userManagement':
        return <UserManagement token={token} />;
      case 'platformSettings':
        return <PlatformSettingsManager token={token} />;
      case 'activityLog': // Added for activity log
        return <ActivityLogViewer token={token} />;
      case 'therapistAssignment': // Added for therapist assignment settings
        return <TherapistAssignmentSettings token={token} />;
      case 'aiModeChange': // Added for AI mode change settings
        return <AiModeChangeSettings token={token} />;
      case 'contentSafety': // Added for content safety screening
        return <ContentSafetySettings token={token} />;
      case 'toolRegistry': // Added for therapist tool registry
        return <TherapistToolRegistry token={token} />;
      case 'personaToolAssignments': // Added for persona tool assignments
        return <PersonaToolAssignments token={token} />;
      case 'assessmentDefinitions': // Added for assessment definitions
        return <AssessmentDefinitionManager token={token} />;
      case 'messageArchival': // Added for message archival settings
        return <MessageArchivalSettings />;
      case 'contextMemory': // Context & memory: episodes, summaries, dossier, redaction
        return <ContextMemorySettings />;
      case 'safetyMonitoring': // Alert notification mode + daily Gen 2 safety summary
        return <SafetyMonitoring />;
      case 'callAudioRetention': // Keep live-call audio for testing, and for how long
        return <CallAudioRetention />;
      case 'signInSessions': // Idle timeout and sign-in length (security batch 2)
        return <SessionSecuritySettings />;
      case 'notifications': // Added for notification configuration
        return <NotificationConfigManager token={token} />;
      case 'websiteEnquiries': // Added for website enquiries
        return <WebsiteEnquiryManager token={token} />;
      case 'ttsConfiguration': // Added for TTS configuration
        return <TTSConfigurationManager token={token} />;
      case 'providerPermissions': // Added for provider permissions
        return <ProviderPermissionsManager token={token} />;
      case 'manageStaff': // Added for staff management
        return <ManageStaff token={token} />;
      case 'consent': // Consent document authoring (AD-01)
        return <ConsentDocumentManager token={token} />;
      case 'schedules': // Therapist slots and bookings (AD-04, D-16)
        return <ScheduleOversight token={token} />;
      case 'serviceGroups': // Added for service groups
        return <ServiceGroupsManagement token={token} />;
      case 'invitations': // Added for client invitations
        return <InvitationManagement token={token} />;
      case 'onboardingFlows': // Added for onboarding flows
        return <OnboardingFlowsManager token={token} />;
      case 'assignmentRules': // Added for assignment rules
        return <AssignmentRulesManager token={token} />;
      case 'triageReview': // Added for triage review
        return <TriageReviewManager token={token} />;
      case 'triageStrategies': // Added for triage strategies
        return <TriageStrategyManager token={token} />;
      case 'alerts': // Added for platform alert management
        return <AlertsDashboard token={token} />;
      case 'clientReviews': // Added for client review statistics
        return <AdminReviewStats token={token} userRole={userRole} />;
      case 'reviewTemplates': // Added for review template management
        return <ReviewTemplateManager token={token} />;
      case 'reviewTypes': // Added for review type management
        return <ReviewTypeManager token={token} />;
      case 'advancedSimulation': // Added for advanced simulation
        return <AdvancedSimulationDashboard token={token} />;
      case 'advancedSimulationBatch':
        return (
          <BatchProgressViewer
            key={currentBatchId}
            batchId={currentBatchId}
            token={token}
          />
        );
      case 'llmDiagnostics': // Added for LLM diagnostics
        return <LLMDiagnostics token={token} />;
      case 'timesOfService': // Added for times of service
        return <TimesOfServiceManager token={token} />;
      case 'researcherManagement': // Added for researcher management
        return <ResearcherManagement token={token} />;
      case 'cbcSettings': // Added for CBC settings
        return <CBCSettingsManager />;
      case 'parasocialDetection': // Added for parasocial detection settings
        return <ParasocialConfigPanel />;
      case 'boundaryMonitoring': // Added for boundary monitoring dashboard
        return <BoundaryMonitoringDashboard token={token} userRole={userRole} />;
      case 'llmConfiguration': // Added for LLM configuration
        return <LLMConfigurationManager token={token} />;
      case 'llmComponentConfig': // Unified per-component/service-group LLM config
        return <LlmConfigManager token={token} />;
      case 'scheduledJobs': // Added for scheduled jobs management
        return <ScheduledJobsManager token={token} />;
      case 'setupChecklist':
        return <SetupChecklist token={token} />;
      case 'setupWizard':
        return <SetupWizard token={token} />;
      case 'supportTickets':
        return <SupportTicketsDashboard token={token} />;
      case 'specialistModules':
        return <SpecialistModuleManager token={token} />;
      case 'trainingScenarios':
        return <TrainingScenarioAdmin token={token} />;
      case 'voiceAndAvatar':
        return <VoiceAndAvatarManager token={token} />;
      case 'simulation':
      default:
        return <SimulationRunner token={token} />;
    }
  };

  const navButtonStyle = {
    padding: '8px 15px',
    margin: '0 5px',
    cursor: 'pointer',
    border: '1px solid #ccc',
    backgroundColor: '#f0f0f0',
    borderRadius: '4px',
  };

  const activeNavButtonStyle = {
    ...navButtonStyle,
    backgroundColor: '#d0d0d0', // Darker for active
    fontWeight: 'bold',
    borderColor: '#bbb'
  };

  return (
    <div data-testid="admin-dashboard">
      {/* <h2>Admin Dashboard</h2> Title removed, will be handled by main app header */}
      <nav style={{ marginBottom: '20px', borderBottom: '1px solid #ccc', paddingBottom: '10px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        <button
          onClick={() => handleNav('run-simulation')}
          style={activeView === 'simulation' ? activeNavButtonStyle : navButtonStyle}
        >
          Run Simulation
        </button>
        <button
          onClick={() => handleNav('advanced-simulation')}
          style={activeView === 'advancedSimulation' ? activeNavButtonStyle : navButtonStyle}
          title="Advanced batch simulation, prompt variations, and clone studio"
        >
          Advanced Simulation
        </button>
        <button
          onClick={() => handleNav('persona-library')}
          style={activeView === 'personaLibrary' ? activeNavButtonStyle : navButtonStyle}
          title="Browse, filter, and clone client personas (faceted + semantic search)"
        >
          Persona Library
        </button>
        <button
          onClick={() => handleNav('persona-library-health')}
          style={activeView === 'personaLibraryHealth' ? activeNavButtonStyle : navButtonStyle}
          title="Library curation snapshot — orphans, near-duplicates, missing fields"
        >
          Library Health
        </button>
        <button
          onClick={() => handleNav('client-editor')}
          style={activeView === 'clientEditor' ? activeNavButtonStyle : navButtonStyle}
        >
          Edit Client Personas
        </button>
        <button
          onClick={() => handleNav('therapist-editor')}
          style={activeView === 'therapistEditor' ? activeNavButtonStyle : navButtonStyle}
          title="Configure AI behavior for all therapist personas (including onboarding assistants)"
        >
          Manage Therapist Personas
        </button>
         <button
           onClick={() => handleNav('history')}
           style={activeView === 'history' ? activeNavButtonStyle : navButtonStyle}
         >
           View Simulation History
         </button>
         <button
           onClick={() => handleNav('review-prompt-editor')}
           style={activeView === 'reviewPromptEditor' ? activeNavButtonStyle : navButtonStyle}
         >
            Edit Review Prompt
         </button>
         <button
           onClick={() => handleNav('user-management')}
           style={activeView === 'userManagement' ? activeNavButtonStyle : navButtonStyle}
         >
           Manage Users
         </button>
         <button
           onClick={() => handleNav('manage-staff')}
           style={activeView === 'manageStaff' ? activeNavButtonStyle : navButtonStyle}
         >
           Manage Staff
         </button>
         <button
           onClick={() => handleNav('platform-settings')}
           style={activeView === 'platformSettings' ? activeNavButtonStyle : navButtonStyle}
         >
           Therapy Settings
         </button>
         <button
           onClick={() => handleNav('activity-log')} // Added for activity log
           style={activeView === 'activityLog' ? activeNavButtonStyle : navButtonStyle}
         >
           Activity Log
         </button>
         <button
           onClick={() => handleNav('therapist-assignment')} // Added for therapist assignment settings
           style={activeView === 'therapistAssignment' ? activeNavButtonStyle : navButtonStyle}
         >
           Therapist Assignment
         </button>
         <button
           onClick={() => handleNav('ai-mode-change')} // Added for AI mode change settings
           style={activeView === 'aiModeChange' ? activeNavButtonStyle : navButtonStyle}
         >
           AI Mode Change Settings
         </button>
         <button
           onClick={() => handleNav('content-safety')} // Added for content safety screening
           style={activeView === 'contentSafety' ? activeNavButtonStyle : navButtonStyle}
           title="Configure content safety screening for journal entries and check-ins"
         >
           Content Safety
         </button>
         <button
           onClick={() => handleNav('tool-registry')} // Added for therapist tool registry
           style={activeView === 'toolRegistry' ? activeNavButtonStyle : navButtonStyle}
         >
           Tool Registry
         </button>
         <button
           onClick={() => handleNav('persona-tool-assignments')} // Added for persona tool assignments
           style={activeView === 'personaToolAssignments' ? activeNavButtonStyle : navButtonStyle}
         >
           Persona Tools
         </button>
         <button
           onClick={() => handleNav('assessment-definitions')}
           style={activeView === 'assessmentDefinitions' ? activeNavButtonStyle : navButtonStyle}
         >
           Manage Assessments
         </button>
         <button
           onClick={() => handleNav('message-archival')}
           style={activeView === 'messageArchival' ? activeNavButtonStyle : navButtonStyle}
         >
           Message Archival
         </button>
         <button
           onClick={() => handleNav('context-memory')}
           style={activeView === 'contextMemory' ? activeNavButtonStyle : navButtonStyle}
         >
           Context &amp; Memory
         </button>
         <button
           onClick={() => handleNav('safety-monitoring')}
           style={activeView === 'safetyMonitoring' ? activeNavButtonStyle : navButtonStyle}
         >
           Safety Monitoring
         </button>
         <button
           onClick={() => handleNav('call-audio-retention')}
           style={activeView === 'callAudioRetention' ? activeNavButtonStyle : navButtonStyle}
         >
           Call Audio Retention
         </button>
         <button
           onClick={() => handleNav('sign-in-sessions')}
           style={activeView === 'signInSessions' ? activeNavButtonStyle : navButtonStyle}
         >
           Sign-in Sessions
         </button>
         <button
           onClick={() => handleNav('notifications')}
           style={activeView === 'notifications' ? activeNavButtonStyle : navButtonStyle}
         >
           Notifications
         </button>
         <button
           onClick={() => handleNav('website-enquiries')}
           style={activeView === 'websiteEnquiries' ? activeNavButtonStyle : navButtonStyle}
         >
           Website Enquiries
         </button>
         <button
           onClick={() => handleNav('tts-configuration')}
           style={activeView === 'ttsConfiguration' ? activeNavButtonStyle : navButtonStyle}
         >
           TTS Configuration
         </button>
         <button
           onClick={() => handleNav('voice-and-avatar')}
           style={activeView === 'voiceAndAvatar' ? activeNavButtonStyle : navButtonStyle}
           title="Admin-managed voice catalog and avatar library (client-facing voices)"
         >
           Voices &amp; Avatars
         </button>
         <button
           onClick={() => handleNav('provider-permissions')}
           style={activeView === 'providerPermissions' ? activeNavButtonStyle : navButtonStyle}
         >
           Provider Permissions
         </button>
         <button
           onClick={() => handleNav('consent')}
           style={activeView === 'consent' ? activeNavButtonStyle : navButtonStyle}
           title="Versioned consent documents clients must agree to"
         >
           Consent
         </button>
         <button
           onClick={() => handleNav('schedules')}
           style={activeView === 'schedules' ? activeNavButtonStyle : navButtonStyle}
         >
           Schedules
         </button>
         <button
           onClick={() => handleNav('service-groups')}
           style={activeView === 'serviceGroups' ? activeNavButtonStyle : navButtonStyle}
         >
           Service Groups
         </button>
         <button
           onClick={() => handleNav('invitations')}
           style={activeView === 'invitations' ? activeNavButtonStyle : navButtonStyle}
         >
           Client Invitations
         </button>
         <button
           onClick={() => handleNav('onboarding-flows')}
           style={activeView === 'onboardingFlows' ? activeNavButtonStyle : navButtonStyle}
         >
           Onboarding Flows
         </button>
         <button
           onClick={() => handleNav('assignment-rules')}
           style={activeView === 'assignmentRules' ? activeNavButtonStyle : navButtonStyle}
         >
           Assignment Rules
         </button>
         <button
           onClick={() => handleNav('triage-review')}
           style={activeView === 'triageReview' ? activeNavButtonStyle : navButtonStyle}
         >
           Triage Review
         </button>
         <button
           onClick={() => handleNav('triage-strategies')}
           style={activeView === 'triageStrategies' ? activeNavButtonStyle : navButtonStyle}
         >
           Triage Strategies
         </button>
         <button
           onClick={() => handleNav('alerts')}
           style={activeView === 'alerts' ? activeNavButtonStyle : navButtonStyle}
           title="Platform-wide alert visibility and management"
         >
           Alerts
         </button>
         <button
           onClick={() => handleNav('client-reviews')}
           style={activeView === 'clientReviews' ? activeNavButtonStyle : navButtonStyle}
           title="Client review system statistics and monitoring"
         >
           Client Reviews
         </button>
         <button
           onClick={() => handleNav('review-templates')}
           style={activeView === 'reviewTemplates' ? activeNavButtonStyle : navButtonStyle}
           title="Manage review template prompts and settings"
         >
           Review Templates
         </button>
         <button
           onClick={() => handleNav('review-types')}
           style={activeView === 'reviewTypes' ? activeNavButtonStyle : navButtonStyle}
           title="Manage review types, context configuration, and reference documents"
         >
           Review Types
         </button>
         <button
           onClick={() => handleNav('llm-diagnostics')}
           style={activeView === 'llmDiagnostics' ? activeNavButtonStyle : navButtonStyle}
           title="Monitor LLM operations, view errors, and investigate issues"
         >
           LLM Diagnostics
         </button>
         <button
           onClick={() => handleNav('llm-configuration')}
           style={activeView === 'llmConfiguration' ? activeNavButtonStyle : navButtonStyle}
           title="Configure LLM providers, models, and generation parameters"
         >
           LLM Configuration
         </button>
         <button
           onClick={() => handleNav('llm-component-config')}
           style={activeView === 'llmComponentConfig' ? activeNavButtonStyle : navButtonStyle}
           title="Per-component and per-service-group model overrides, and which layer is in force"
         >
           LLM Component Config
         </button>
         <button
           onClick={() => handleNav('times-of-service')}
           style={activeView === 'timesOfService' ? activeNavButtonStyle : navButtonStyle}
           title="Configure service hours, emergency access, and out-of-hours behavior"
         >
           Times of Service
         </button>
         <button
           onClick={() => handleNav('researcher-management')}
           style={activeView === 'researcherManagement' ? activeNavButtonStyle : navButtonStyle}
           title="Manage researcher roles, budgets, and sandbox persona promotion"
         >
           Researcher Management
         </button>
         <button
           onClick={() => handleNav('cbc-settings')}
           style={activeView === 'cbcSettings' ? activeNavButtonStyle : navButtonStyle}
           title="Conversational Boundary Control - Configure therapeutic drift detection and reconnection"
         >
           CBC Settings
         </button>
         <button
           onClick={() => handleNav('parasocial-detection')}
           style={activeView === 'parasocialDetection' ? activeNavButtonStyle : navButtonStyle}
           title="Parasocial Detection - Configure AI dependency and attachment pattern detection"
         >
           Parasocial Detection
         </button>
         <button
           onClick={() => handleNav('boundary-monitoring')}
           style={activeView === 'boundaryMonitoring' ? activeNavButtonStyle : navButtonStyle}
           title="Monitor therapeutic drift and parasocial detection patterns across clients"
         >
           Boundary Monitoring
         </button>
         <button
           onClick={() => handleNav('scheduled-jobs')}
           style={activeView === 'scheduledJobs' ? activeNavButtonStyle : navButtonStyle}
           title="Manage scheduled jobs for automated tasks (Cloud Scheduler & node-cron)"
         >
           Scheduled Jobs
         </button>
         <button
           onClick={() => handleNav('setup-checklist')}
           style={activeView === 'setupChecklist' ? activeNavButtonStyle : navButtonStyle}
           title="View platform configuration health and broken references"
         >
           Setup Checklist
         </button>
         <button
           onClick={() => handleNav('setup-wizard')}
           style={activeView === 'setupWizard' ? activeNavButtonStyle : navButtonStyle}
           title="AI-powered setup wizard for creating onboarding configurations"
         >
           Setup Wizard
         </button>
         <button
           onClick={() => handleNav('support-tickets')}
           style={activeView === 'supportTickets' ? activeNavButtonStyle : navButtonStyle}
           title="Client support ticket management"
         >
           Support Tickets
         </button>
         <button
           onClick={() => handleNav('specialist-modules')}
           style={activeView === 'specialistModules' ? activeNavButtonStyle : navButtonStyle}
           title="Manage Gen 2 specialist knowledge modules for transdiagnostic consultations"
         >
           Specialist Modules
         </button>
         <button
           onClick={() => handleNav('training-scenarios')}
           style={activeView === 'trainingScenarios' ? activeNavButtonStyle : navButtonStyle}
           title="Manage training scenarios across all organisations"
         >
           Training Scenarios
         </button>
      </nav>

      {renderActiveView()}
    </div>
  );
};

export default AdminDashboard;
